import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { WalletButton } from '../WalletButton';
import { Button, Drawer, Menu } from 'antd';
import { MenuOutlined, GlobalOutlined, CloseOutlined } from '@ant-design/icons';
import './header.css';
import Container from '../Container';

interface HeaderProps {
  className?: string;
  onConnect?: () => void;
}

interface NavItem {
  key: string;
  label: string;
  link: string;
  isExternal?: boolean;
  active?: boolean;
}

const Header: React.FC<HeaderProps> = ({ className = '', onConnect }) => {
  const { t, i18n } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems: NavItem[] = [
    { key: 'staking', label: t('nav-staking'), link: 'https://www.48.club/staking' },
    { key: 'nft', label: t('nav-nft'), link: 'https://www.48.club/nft' },
    { key: 'soul', label: t('nav-soul'), link: 'https://soul.48.club/soul', active: true },
    { key: 'voting', label: t('nav-voting'), link: 'https://www.48.club/voting' },
    { key: 'farm', label: t('nav-farm'), link: 'https://www.48.club/pool' },
    { key: 'trade', label: t('nav-trade'), link: 'https://www.48.club/trade-race' },
    { key: 'delegate', label: t('app_header_menu_node_title'), link: 'https://www.48.club/delegate' },
  ];

  const moreItems: NavItem[] = [
    { key: 'home', label: t('app_header_menu_home_title'), link: 'https://www.48.club' },
    { 
      key: 'inscriptions', 
      label: t('app_header_menu_inscriptions_title'), 
      link: 'https://www.48.club/inscriptions',
      isExternal: true 
    },
    { key: 'committee', label: t('app_header_menu_committee_title'), link: 'https://www.48.club/committee' },
    { key: 'node', label: t('app_header_menu_node_title'), link: 'https://www.48.club/validation-node' },
    { key: 'milestone', label: t('app_header_menu_milestone_title'), link: 'https://www.48.club/milestome' },
    { key: 'docs', label: t('app_header_menu_docs_title'), link: 'https://docs.48.club', isExternal: true },
  ];

  const toggleLanguage = () => {
    const currentLang = i18n.language;
    i18n.changeLanguage(currentLang === 'en' ? 'zh' : 'en');
  };

  const renderNavItem = (item: NavItem) => {
    if (item.isExternal) {
      return (
        <a 
          key={item.key}
          href={item.link}
          target="_blank"
          rel="noopener noreferrer"
          className={`text-14 ${item.active ? 'text-yellow-400' : 'text-white'}`}
        >
          {item.label}
        </a>
      );
    }
    
    return (
      <Link 
        key={item.key}
        to={item.link}
        className={`text-14 ${item.active ? 'text-yellow-400' : 'text-white'}`}
      >
        {item.label}
      </Link>
    );
  };

  // 移动端菜单项，More 子菜单默认展开
  const mobileMenuItems = [
    ...navItems.map(item => ({
      key: item.key,
      label: item.isExternal ? (
        <a href={item.link} target="_blank" rel="noopener noreferrer">
          {item.label}
        </a>
      ) : (
        <Link to={item.link} onClick={() => setMobileMenuOpen(false)}>
          {item.label}
        </Link>
      ),
    })),
    {
      key: 'more',
      label: t('nav-more'),
      className: 'more-menu text-white bg-tranparent',
      children: moreItems.map(item => ({
        key: item.key,
        className: 'more-menu-item',
        label: item.isExternal ? (
          <a href={item.link} target="_blank" rel="noopener noreferrer">
            {item.label}
          </a>
        ) : (
          <Link to={item.link} onClick={() => setMobileMenuOpen(false)}>
            {item.label}
          </Link>
        ),
      })),
    },
  ];

  return (
    <header className={`w-full ${className} fixed top-0 z-50 py-[12px] bg-[#000]`}>
      {/* Desktop Header */}
      <div className="hidden sm:block">
        <Container className="flex items-center justify-between px-[12px]">
          {/* Left Navigation */}
          <nav className="flex items-center space-x-[28px]">
            {navItems.map(renderNavItem)}
            <div className="relative group">
              <button className="text-white text-14 flex items-center transition-colors">
                {t('nav-more')}
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {/* Dropdown Menu */}
              <div className="absolute top-full left-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="py-2">
                  {moreItems.map(item => (
                    <div key={item.key} className="px-4 py-2">
                      {item.isExternal ? (
                        <a 
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-700 dark:text-gray-300 block"
                        >
                          {item.label}
                        </a>
                      ) : (
                        <Link 
                          to={item.link}
                          className="text-gray-700 dark:text-gray-300 block"
                        >
                          {item.label}
                        </Link>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </nav>

          {/* Center Logo */}
          <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center">
            <Link to="https://www.48.club" className="flex items-center">
              <img 
                src="/logo-white.png" 
                alt="48CLUB" 
                className="h-8"
              />
            </Link>

          </div>

          {/* Right Wallet Button */}
          <div className="flex items-center">
            <button
              onClick={toggleLanguage}
              className="mr-6 text-white text-14 transition-colors flex items-center"
            >
              <GlobalOutlined className="mr-1" />
              {i18n.language === 'en' ? '中文' : 'EN'}
            </button>
            <WalletButton />
          </div>
        </Container>
      </div>

      {/* Mobile Header */}
      <div className="sm:hidden">
        <div className="px-4 h-14 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src="/logo-white.png" 
              alt="48CLUB" 
              className="h-6"
            />
          </Link>

          {/* Right Section */}
          <div className="flex items-center space-x-3">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="text-white text-sm transition-colors"
            >
              {i18n.language === 'en' ? '中文' : 'EN'}
            </button>

            {/* Wallet Button */}
            <WalletButton />

            {/* Mobile Menu Button */}
            <Button
              type="text"
              icon={<MenuOutlined />}
              className="text-white border-none hover:!text-white"
              onClick={() => setMobileMenuOpen(true)}
            />
          </div>
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      <Drawer
        title="菜单"
        placement="right"
        onClose={() => setMobileMenuOpen(false)}
        open={mobileMenuOpen}
        className="sm:hidden text-white mobile-menu"
        closeIcon={<CloseOutlined style={{ color: '#fff' }} />}
        styles={{
          body: { padding: 0 },
          header: { 
            background: '#1f2937',
            borderBottom: '1px solid #374151'
          },
          content: { background: '#1f2937', color: '#fff' },
        }}
      >
        <Menu
          mode="inline"
          items={mobileMenuItems}
          defaultOpenKeys={['more']} // 默认展开 More 子菜单
          className="bg-transparent border-none mobile-menu"
          style={{ background: 'transparent' }}
          theme="dark"
        />
      </Drawer>
    </header>
  );
};

export default Header; 